import React, { useContext, useState, useEffect, useCallback } from "react";
import { Modal, Button, Col, Row, Alert, Table } from "react-bootstrap";
import { NavLink } from "react-router";
import Icon from './Icon';
import { faSignOutAlt as logOutOutline, faCheckCircle, faSun, faMoon, faCircleHalfStroke, faUserPlus, faCoins, faHistory } from '@fortawesome/free-solid-svg-icons';
import { logout } from "./utils/logout";
import version from "../ver.json";
import { UserContext } from "./auth/auth";
import { useLicense } from "../hooks/useLicense";
import { getDaysRemaining } from "@/key";
import { useNavigate } from "react-router";
import { useTheme } from "../contexts/themeContext";
import { WebSocketContext } from "../websocket/websocket";
import { BonusBalance, BonusTransactionsResponse } from "@/types/user";

interface ProfileModalProps {
    show: boolean;
    onHide: () => void;
}

export default function ProfileModal({ show, onHide }: ProfileModalProps) {
    const navigate = useNavigate();
    const user = React.useContext(UserContext);
    const { theme, setTheme } = useTheme();
    const email = user.email || "";
    const avatarUrl = user.avatar || '/logo.png';

    // Получаем информацию о лицензии из useLicense
    const { licenseInfo } = useLicense();

    // Состояние для кнопки "Пригласить друзей"
    const [copied, setCopied] = useState(false);

    // Состояние для бонусов
    const wsContext = useContext(WebSocketContext);
    const isConnected = wsContext?.connected ?? false;
    const [bonusBalance, setBonusBalance] = useState<BonusBalance | null>(null);
    const [bonusTransactions, setBonusTransactions] = useState<BonusTransactionsResponse | null>(null);
    const [bonusLoading, setBonusLoading] = useState(false);
    const [bonusError, setBonusError] = useState<string | null>(null);

    const fetchBonusData = useCallback(async () => {
        if (!isConnected) return;
        setBonusLoading(true);
        setBonusError(null);
        try {
            const [balanceRes, transactionsRes] = await Promise.all([
                fetch('/api/bonus/balance'),
                fetch('/api/bonus/transactions?limit=50&offset=0'),
            ]);
            if (balanceRes.ok) {
                setBonusBalance(await balanceRes.json());
            }
            if (transactionsRes.ok) {
                setBonusTransactions(await transactionsRes.json());
            }
        } catch (err) {
            setBonusError('Не удалось загрузить бонусы');
            console.error('Failed to fetch bonus data:', err);
        } finally {
            setBonusLoading(false);
        }
    }, [isConnected]);

    useEffect(() => {
        if (show && isConnected) {
            fetchBonusData();
        } else {
            setBonusBalance(null);
            setBonusTransactions(null);
        }
    }, [show, isConnected, fetchBonusData]);

    const daysRemaining = licenseInfo?.planExp ? getDaysRemaining(licenseInfo.planExp) : 0;

    // Хелпер для приглашения друзей
    const handleInviteFriends = async () => {
        if (!user.ref_key) return;
        const shareUrl = `${window.location.origin}/r/${user.ref_key}`;
        const shareData = {
            title: 'Artifacts — управление жизненными целями',
            text: 'Попробуй Artifacts — удобное приложение для постановки целей и планирования!',
            url: shareUrl,
        };

        // Web Share API (мобильные устройства)
        if (navigator.share && /Mobi|Android/i.test(navigator.userAgent)) {
            try {
                await navigator.share(shareData);
                return;
            } catch {
                // Пользователь отменил — fallback на копирование
            }
        }

        // Fallback: копирование в буфер
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            // Если clipboard API недоступен — просто открываем ссылку
            window.open(shareUrl, '_blank');
        }
    };

    return (
        <Modal show={show} onHide={onHide}>
            <Modal.Header closeButton>
                <Modal.Title>Профиль</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Row className="d-flex justify-content-center">
                    <Col xs={12}>
                        <div className="d-flex flex-column gap-3 align-items-center">
                            {email && (
                                <>
                                    <img
                                        src={avatarUrl}
                                        alt="Avatar"
                                        width={100}
                                        height={100}
                                        className="rounded-circle"
                                    />
                                    <div className="text-center">
                                        <strong>{email}</strong>
                                    </div>
                                </>
                            )}
                            {user.ref_key && (
                                <>
                                    <Button
                                        variant={copied ? "success" : "outline-success"}
                                        size="lg"
                                        onClick={handleInviteFriends}
                                        disabled={copied}
                                    >
                                        <Icon icon={copied ? faCheckCircle : faUserPlus} className="me-2" />
                                        {copied ? "Скопировано" : "Пригласить друзей"}
                                    </Button>
                                    <Alert variant="info"><small style={{ maxWidth: 300 }}>
                                        Приглашая друга, вы получаете бонусы. За счет бонусов можно уменьшать стоимость подписки, списывая 1 бонус = 1 рубль. Пригласив трех друзей, которые купят подписку, Вы сможете полностью оплачивать подписку за счет бонусов.
                                    </small>
                                    </Alert>
                                </>
                            )}

                            {/* Бонусные баллы — только для онлайн */}
                            {isConnected && (
                                <div className="w-100 mt-3">
                                    <hr />
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h5 className="mb-0">
                                            <Icon icon={faCoins} className="me-2 text-warning" />
                                            Бонусные баллы
                                        </h5>
                                    </div>

                                    {bonusLoading ? (
                                        <div className="text-center py-3 text-muted">
                                            Загрузка...
                                        </div>
                                    ) : bonusError ? (
                                        <Alert variant="danger" className="text-center">
                                            {bonusError}
                                        </Alert>
                                    ) : bonusBalance ? (
                                        <>
                                            <div className="d-flex align-items-center justify-content-center mb-3">
                                                <div className="text-center" style={{ minWidth: 120 }}>
                                                    <div className="display-6 fw-bold text-warning">
                                                        {bonusBalance.balance.toFixed(2)}
                                                    </div>
                                                    <small className="text-muted">
                                                        обновлено {new Date(bonusBalance.updated_at).toLocaleString('ru-RU')}
                                                    </small>
                                                </div>
                                            </div>

                                            {bonusTransactions && bonusTransactions.items.length > 0 && (
                                                <div>
                                                    <h6 className="mb-2">
                                                        <Icon icon={faHistory} className="me-1" />
                                                        История начислений
                                                    </h6>
                                                    <div className="table-responsive" style={{ maxHeight: 300, overflowY: 'auto' }}>
                                                        <Table striped bordered hover size="sm" className="mb-0">
                                                            <thead>
                                                                <tr>
                                                                    <th>Дата</th>
                                                                    <th>Описание</th>
                                                                    <th>Сумма</th>
                                                                    <th>Баланс</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {bonusTransactions.items.map((tx) => (
                                                                    <tr key={tx.id}>
                                                                        <td style={{ whiteSpace: 'nowrap' }}>
                                                                            {new Date(tx.created_at).toLocaleString('ru-RU')}
                                                                        </td>
                                                                        <td>
                                                                            <small className="text-muted">{tx.description}</small>
                                                                        </td>
                                                                        <td>
                                                                            <span className={tx.amount >= 0 ? 'text-success' : 'text-danger'}>
                                                                                {tx.amount > 0 ? '+' : ''}{tx.amount.toFixed(2)}
                                                                            </span>
                                                                        </td>
                                                                        <td className="fw-semibold">
                                                                            {tx.balance_after.toFixed(2)}
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </Table>
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    ) : null}
                                </div>
                            )}
                        </div>

                        <div className="mt-4">
                            <hr />
                            <h5 className="mb-3">Тема оформления</h5>
                            <div className="d-flex gap-2 justify-content-center">
                                <Button
                                    variant={theme === 'light' ? 'primary' : 'outline-secondary'}
                                    size="sm"
                                    onClick={() => setTheme('light')}
                                >
                                    <Icon icon={faSun} className="me-1" />
                                    Светлая
                                </Button>
                                <Button
                                    variant={theme === 'dark' ? 'primary' : 'outline-secondary'}
                                    size="sm"
                                    onClick={() => setTheme('dark')}
                                >
                                    <Icon icon={faMoon} className="me-1" />
                                    Тёмная
                                </Button>
                                <Button
                                    variant={theme === 'auto' ? 'primary' : 'outline-secondary'}
                                    size="sm"
                                    onClick={() => setTheme('auto')}
                                >
                                    <Icon icon={faCircleHalfStroke} className="me-1" />
                                    Авто
                                </Button>
                            </div>
                        </div>

                        {/* Информация о подписке */}
                        {licenseInfo && (
                            <div className="mt-4">
                                <hr />
                                <h5 className="mb-3">Информация о подписке</h5>

                                {licenseInfo.plan.toLowerCase() !== 'free' && (
                                    <div className="text-center mb-3">
                                        <div className="display-6 fw-bold text-primary">{daysRemaining}</div>
                                        <small className="text-muted">дней до истечения</small>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Выход и версия — в самом низу */}
                        <div className="mt-4 text-center">
                            <Button
                                variant="outline-danger"
                                size="lg"
                                onClick={() => {
                                    onHide();
                                    logout();
                                }}
                            >
                                <Icon icon={logOutOutline} className="me-2" />
                                Выйти из системы
                            </Button>
                            <div className="text-center mt-3 text-muted">
                                <small>Версия приложения: {version.ver}</small>
                            </div>
                            <div className="text-center mt-2">
                                <small className="d-block">
                                    <NavLink
                                        to="/privacy"
                                        className="text-muted text-decoration-underline d-block"
                                        onClick={onHide}
                                    >
                                        Политика обработки персональных данных
                                    </NavLink>
                                    <NavLink
                                        to="/terms"
                                        className="text-muted text-decoration-underline d-block"
                                        onClick={onHide}
                                    >
                                        Пользовательское соглашение
                                    </NavLink>
                                    <NavLink
                                        to="/refund"
                                        className="text-muted text-decoration-underline d-block"
                                        onClick={onHide}
                                    >
                                        Правила возврата денежных средств
                                    </NavLink>
                                    <NavLink
                                        to="/cookie-policy"
                                        className="text-muted text-decoration-underline d-block"
                                        onClick={onHide}
                                    >
                                        Политика использования файлов Cookie
                                    </NavLink>
                                </small>
                            </div>
                        </div>
                    </Col>
                </Row>
            </Modal.Body>

            {/* Тост "ссылка скопирована" — рендерится в layout */}
        </Modal>
    );
}